import { ApolloQueryResult, QueryOptions, useApolloClient } from '@apollo/client';
import { DocumentNode } from 'graphql';
import omit from 'lodash.omit';
import { NextRouter, useRouter } from 'next/router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Url } from 'url';
import { MeDocument, MeQuery } from '../generated/graphql';
import { ACCESS_TOKEN, login, refresh, REFRESH_TOKEN } from '../utils/auth';
import getStorageWithExpiry from '../utils/getStorageWithExpiry';

type RedirectFn = (router: NextRouter) => Url | string;

export type UseAuthOptions = {
    /**
     * What to do when the authorizing fails? When given true it redirects to login with the requested url to be restored after login.
     */
    onFail?: RedirectFn | boolean;
    /**
     * What to do when the authorizing succeeds?
     */
    onSucces?: RedirectFn;
    authQuery?: DocumentNode;
    onAfterLoginSuccess?: (router: NextRouter) => void;
    // don't trigger onFail when credentials are missing
    ignoreMissingCredentials?: boolean;
};

const isLoginRequested = (authCode: unknown): authCode is string => typeof authCode === 'string';

/**
 * Get the auth status for the user.
 * The withAuth HOC extends this hook and allows you to render a loading state.
 */
export const useAuth = (options?: UseAuthOptions) => {
    const router = useRouter();
    const withAuthCode = isLoginRequested(router.query.authCode);
    const client = useApolloClient();
    const [loading, setLoading] = useState(false);
    const called = useRef(false);
    const succesUrl = useMemo(() => options?.onSucces?.(router), [options, router]);

    const failUrl = useMemo(() => {
        if (typeof options?.onFail === 'function') {
            return options.onFail(router);
        }

        if (typeof options?.onFail === 'boolean' && options.onFail) {
            // when deeplinking lets start with a clean state
            const [nextPath] = router.asPath.split('?');
            return { pathname: `/login`, query: { next: `/${router.locale}/${nextPath}` } };
        }

        return undefined;
    }, [options, router]);

    const authQuery = options?.authQuery ?? MeDocument;
    const result = useRef<ApolloQueryResult<MeQuery>>();

    const fetch = useCallback(
        (opts: Omit<QueryOptions, 'query'> = {}) =>
            // @ts-ignore
            client.query<MeQuery>({ query: authQuery, fetchPolicy: 'cache-first', errorPolicy: 'ignore', ...opts }),
        [authQuery, client]
    );

    const removeAuthCode = useCallback(async () => {
        await router.replace({ pathname: router.pathname, query: omit(router.query, 'authCode') }, undefined, {
            shallow: true,
        });
    }, [router]);

    const handleOnFail = useCallback(async () => {
        if (failUrl === 'ignore') {
            setLoading(false);
            return;
        }

        if (failUrl) {
            await router.replace(failUrl);
        } else {
            setLoading(false);
        }
    }, [failUrl, router]);

    const handleMeCheck = useCallback(async () => {
        const storageUtils = getStorageWithExpiry('local');
        if (!storageUtils) {
            return;
        }
        called.current = true;

        if (withAuthCode) {
            const success = await login(router.query.authCode as string);
            if (success) {
                options?.onAfterLoginSuccess?.(router);
            }
            await removeAuthCode();
        }

        if (!storageUtils.getItem(ACCESS_TOKEN) && !storageUtils.getItem(REFRESH_TOKEN)) {
            if (!options?.ignoreMissingCredentials) {
                await handleOnFail();
            } else {
                setLoading(false);
            }
            return;
        }

        if (!storageUtils.getItem(ACCESS_TOKEN)) {
            const success = await refresh();
            if (!success) {
                await handleOnFail();
                return;
            }
        }

        result.current = await fetch();

        if (!result.current.data.viewer) {
            const success = await refresh();
            if (!success) {
                await handleOnFail();
                return;
            }
            result.current = await fetch({ fetchPolicy: 'network-only' });

            if (!result.current.data.viewer) {
                await handleOnFail();
                return;
            }
        }

        if (result.current.data.viewer) {
            if (succesUrl) {
                await router.replace(succesUrl);
            } else {
                setLoading(false);
            }
        }
    }, [withAuthCode, fetch, router, removeAuthCode, options, handleOnFail, succesUrl]);

    useEffect(() => {
        if (router.isReady && !called.current) {
            setLoading(true);
        }
    }, [router.isReady]);

    useEffect(() => {
        if (loading && !called.current) {
            handleMeCheck();
        }
    }, [handleMeCheck, loading]);

    return {
        failUrl,
        showSpinner: loading || !called.current,
        isAuthorized: !!result.current?.data.viewer,
    };
};
